import { createContext } from 'react';
import { audiencesOf, type JwtPayload } from '@/utils/jwt';
import type { SessionEndReason } from './authStore';

export type AuthStatus = 'booting' | 'authed' | 'unauthed';

/** The reseller-member ladder (H §4.4): OWNER and ADMIN may write; STAFF is read-only. */
export type ResellerRole = 'OWNER' | 'ADMIN' | 'STAFF';

/** The audience a reseller-console token carries (H §1). */
const RESELLER_AUDIENCE = 'journix-reseller';
const RESELLER_PRINCIPAL_TYPE = 'RESELLER';

export interface CurrentOperator {
  email: string;
  name: string | undefined;
  /** From the token until `ResellerProvider` loads tmm-api's live role, which then wins. */
  role: ResellerRole;
  /** OWNER or ADMIN. Presentation only — the servers still answer 403. */
  canWrite: boolean;
  /** OWNER — the only role that sees the GHL integration. */
  isOwner: boolean;
  /** The token's `resellerOf`, as a string. */
  resellerId: string;
}

export interface AuthContextValue {
  status: AuthStatus;
  /** The signed-in reseller member, or `null` — signed out, or a token the gate refuses. */
  operator: CurrentOperator | null;
  /**
   * Why the session ended on its own (`expired` / `revoked` / `misconfigured`), or `null`.
   * Drives the global interstitials in `AuthGate`. Cleared on a successful (re-)auth so it
   * can never get stuck.
   */
  sessionEnd: SessionEndReason | null;
  setAuthed: () => void;
  signOut: () => void;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

/** Normalize a role string onto the ladder; anything else is `null`. */
export function asResellerRole(value: unknown): ResellerRole | null {
  if (typeof value !== 'string') return null;
  const role = value.replace(/^ROLE_/, '').toUpperCase();
  return role === 'OWNER' || role === 'ADMIN' || role === 'STAFF' ? role : null;
}

/** The operator for `role` — the single place `canWrite` / `isOwner` are derived. */
export function operatorWithRole(operator: CurrentOperator, role: ResellerRole): CurrentOperator {
  return { ...operator, role, canWrite: role !== 'STAFF', isOwner: role === 'OWNER' };
}

/**
 * Decode the gate (H §4.4). Admit ONLY a token whose `aud` includes `journix-reseller`,
 * whose `principal_type` is `RESELLER`, and which names its reseller (`resellerOf`); every
 * other token (a tenant-staff token, a customer token, a malformed one) yields `null`. A
 * missing or unknown `resellerRole` is treated as STAFF — the least privilege — until the live
 * role arrives. Presentation only: the servers decide.
 */
export function deriveOperator(claims: JwtPayload | null): CurrentOperator | null {
  if (!claims) return null;
  if (!audiencesOf(claims).includes(RESELLER_AUDIENCE)) return null;
  if (claims.principal_type !== RESELLER_PRINCIPAL_TYPE) return null;
  const resellerOf = claims.resellerOf;
  if (
    !(typeof resellerOf === 'number' && Number.isFinite(resellerOf)) &&
    !(typeof resellerOf === 'string' && resellerOf.length > 0)
  ) {
    return null;
  }
  const base: CurrentOperator = {
    email: claims.email ?? claims.sub ?? '',
    name: claims.name,
    role: 'STAFF',
    canWrite: false,
    isOwner: false,
    resellerId: String(resellerOf),
  };
  return operatorWithRole(base, asResellerRole(claims.resellerRole) ?? 'STAFF');
}
